import React, { useCallback } from 'react';

import styles from './styles.module.css';
import {
  formatDateForDisplay,
  formatDateInputValueForReadOnly
} from '@/solaris-lens-common-lib/src/utils/dates';

interface DateInputProps {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  type?: 'date' | 'datetime-local';
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  id?: string;
  readOnly?: boolean;
  customClass?: string;
  min?: string;
  max?: string;
  variant?: 'default' | 'compact';
}

const DateInput: React.FC<DateInputProps> = ({
  label,
  value,
  onChange = () => {},
  type = 'date',
  required = false,
  disabled = false,
  error,
  id,
  readOnly = false,
  customClass,
  min,
  max,
  variant = 'default'
}) => {
  const inputId =
    id || `date-input-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const formGroupClass =
    `${styles.formGroup} ${variant === 'compact' ? styles.formGroupCompact : ''} ${customClass || ''}`.trim();
  const labelClass =
    `${styles.label} ${variant === 'compact' ? styles.labelCompact : ''}`.trim();
  const inputClass =
    `${styles.input} ${variant === 'compact' ? styles.inputCompact : ''} ${error ? styles.inputError : ''}`.trim();

  const clampDateTime = useCallback(
    (inputValue: string): string => {
      if (!inputValue) return inputValue;

      const selectedDate = new Date(inputValue);

      if (min) {
        const minDate = new Date(min);
        if (selectedDate < minDate) {
          return min;
        }
      }

      if (max) {
        const maxDate = new Date(max);
        if (selectedDate > maxDate) {
          return max;
        }
      }

      return inputValue;
    },
    [min, max]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const clampedValue = clampDateTime(e.target.value);
      onChange(clampedValue);
    },
    [clampDateTime, onChange]
  );

  const formatDisplayValue = (dateValue: string) => {
    if (!dateValue) return '';

    try {
      if (type === 'datetime-local') {
        const date = new Date(dateValue);
        if (variant === 'compact' && customClass?.includes('dateInput')) {
          return formatDateForDisplay(dateValue);
        }
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return formatDateInputValueForReadOnly(dateValue);
    } catch {
      return dateValue;
    }
  };

  return (
    <div className={formGroupClass}>
      <label
        id={`${label}-date-input-label`}
        htmlFor={inputId}
        className={labelClass}
      >
        {label}
        {required && <span className={styles.required}>*</span>}
      </label>
      {readOnly ? (
        <div className={styles.inputReadOnly}>
          {value &&
          typeof value === 'string' &&
          value.includes('/') &&
          value.includes(':') &&
          (value.includes('AM') || value.includes('PM'))
            ? value
            : formatDisplayValue(value)}
        </div>
      ) : (
        <input
          id={inputId}
          type={type}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          min={min}
          max={max}
          className={inputClass}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : undefined}
        />
      )}
    </div>
  );
};

export default DateInput;
